import {
  Question,
  type Questionnaire,
  type QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import { InferType, string } from 'yup'
import { formatDate, parseDate } from '@/lib/utils'
import { addDays, differenceInCalendarDays } from 'date-fns'
import { PatientProfileValidator } from '@/lib/apis/validators'
import { McType, MedicalCertificateMetadata } from '@/lib/apis/types'
import { mustAgree } from '@/shared/forms/YupValidators'

export namespace MedicalCertificate {
  const SickLeavePageOne: QuestionnairePage = {
    questions: [
      Question.date(
        'onDate',
        'Requesting a medical certificate for the date',
        false,
        {
          min: 0,
          max: 1,
        },
      ),
      Question.singleChoice('mcFor', 'For', [
        { id: 'Work', label: 'Work' },
        {
          id: 'Study',
          label: 'School/University',
          disclaimer:
            "This certificate covers attendance only — it can't be used to defer an assessment or exam. For that, you'll need a general consultation with a doctor.",
        },
      ]),
      Question.yesNo(
        'requiresDoctorVisit',
        'Do you feel unfit for work/study but doctor visit or long telehealth consultation is unnecessary?',
      ),
      Question.singleChoice('symptoms', 'You are suffering from', [
        { id: 'COMMON_COLD', label: 'Common cold/Flu/Covid' },
        { id: 'PERIOD_PAIN', label: 'Period pain' },
        {
          id: 'MILD_LOWER_BACK_PAIN',
          label: 'Mild lower back pain due to muscle strain',
        },
        {
          id: 'MILD_HEADACHE',
          label: 'Mild headache / Migraine (same as before)',
        },
        { id: 'MINOR_INJURY', label: 'Minor injury' },
        {
          id: 'SORE_THROAT',
          label: 'Sore throat but tolerating food and drink',
        },
        { id: 'DIARRHOEA', label: 'Diarrhoea without blood and mucous' },
        { id: 'TOOTHACHE', label: 'Toothache awaiting dentist review' },
        { id: 'EXHAUSTING', label: 'Exhaustion/Stress' },
        { id: 'OTHERS', label: 'Other mild symptoms' },
      ]),
    ],
  }

  const SickLeaveMultiDayPageOne: QuestionnairePage = {
    questions: [
      Question.dateRange(
        'onDate',
        'toDate',
        'Requesting a medical certificate from the date',
        'To date (inclusive)',
        false,
        {
          minStart: -1,
          maxStart: 0,
          minRange: 1,
          maxRange: 1,
        },
      ),
      ...SickLeavePageOne.questions.slice(1),
    ],
  }

  const SickLeavePageOneWithOtherSymptoms: QuestionnairePage = {
    questions: [
      ...SickLeavePageOne.questions,
      Question.freeText(
        'mcSymptom',
        'Please describe your symptoms, duration and associated symptoms.',
      ),
    ],
  }

  const SickLeaveMultiDayPageOneWithOtherSymptoms: QuestionnairePage = {
    questions: [
      ...SickLeaveMultiDayPageOne.questions,
      Question.freeText(
        'mcSymptom',
        'Please describe your symptoms, duration and associated symptoms.',
      ),
    ],
  }

  const SickLeavePageTwo: QuestionnairePage = {
    questions: [
      Question.yesNo(
        'hasSeriousSymptoms',
        'Do you have the following symptoms?\n' +
          '- Chest pain/palpitation/shortness of breath\n' +
          '- Weakness and changes in sensation in any part of the body\n' +
          '- Blurred vision/Slurred speech\n' +
          '- Headache associated with decreased alertness and is different from previously diagnosed migraine/tension headache\n' +
          '- Neck stiffness\n' +
          '- Rashes that don’t fade when you press on it\n' +
          '- Excessive vomiting/not tolerating oral intake\n' +
          '- High fever > 39°C with rigors\n' +
          '- Self-harm thoughts/suicidal ideation\n',
      ),
      Question.yesNo(
        'hasPastMedicalConditions',
        'Do you have any significant current and/or past medical conditions?',
        false,
      ),
    ],
  }

  const SickLeavePageTwoWithPastMedicalConditions: QuestionnairePage = {
    questions: [
      ...SickLeavePageTwo.questions,
      Question.freeText(
        'pastMedicalConditions',
        'Please list any significant current and/or past medical conditions.',
      ),
    ],
  }

  const SickLeavePageThree: QuestionnairePage = {
    questions: [
      Question.yesNo(
        'mcSeeGpAgreement',
        'If you don’t get better, will you go see a GP in person within the next 24-48 hours?',
      ),
      Question.yesNo('mcInlandAgreement', 'Are you in Australia?'),
      Question.yesNo(
        'mcTnC',
        'Have you read and accept the terms and conditions?',
      ),
    ],
  }

  const CarerLeavePageOne: QuestionnairePage = {
    questions: [
      Question.date(
        'onDate',
        'Requesting a carer certificate for the date',
        false,
        {
          min: 0,
          max: 1,
        },
      ),
      Question.singleChoice('mcFor', 'For', [
        { id: 'Work', label: 'Work' },
        {
          id: 'Study',
          label: 'School/University',
          disclaimer:
            "This certificate covers attendance only — it can't be used to defer an assessment or exam. For that, you'll need a general consultation with a doctor.",
        },
      ]),
      Question.singleChoice(
        'mcCarerFor',
        "Requesting a carer certificate because I'm looking after my unwell",
        [
          { id: 'FamilyMember', label: 'Family Member' },
          {
            id: 'Partner',
            label: 'Partner',
          },
          { id: 'Housemate', label: 'Housemate' },
        ],
      ),
      Question.yesNo(
        'requiresDoctorVisit',
        'Do they need a doctor visit or long telehealth consultation?',
      ),
      Question.singleChoice('symptoms', 'What do they have?', [
        { id: 'COMMON_COLD', label: 'Common cold/Flu/Covid' },
        { id: 'PERIOD_PAIN', label: 'Period pain' },
        {
          id: 'MILD_LOWER_BACK_PAIN',
          label: 'Mild lower back pain due to muscle strain',
        },
        {
          id: 'MILD_HEADACHE',
          label: 'Mild headache / Migraine (same as before)',
        },
        { id: 'MINOR_INJURY', label: 'Minor injury' },
        {
          id: 'SORE_THROAT',
          label: 'Sore throat but tolerating food and drink',
        },
        { id: 'DIARRHOEA', label: 'Diarrhoea without blood and mucous' },
        { id: 'TOOTHACHE', label: 'Toothache awaiting dentist review' },
        { id: 'EXHAUSTING', label: 'Exhaustion/Stress' },
        { id: 'OTHERS', label: 'Other mild symptoms' },
      ]),
    ],
  }

  const CarerLeaveMultiDayPageOne: QuestionnairePage = {
    questions: [
      Question.dateRange(
        'onDate',
        'toDate',
        'Requesting a carer certificate from the date',
        'To date (inclusive)',
        false,
        {
          minStart: -1,
          maxStart: 0,
          minRange: 1,
          maxRange: 1,
        },
      ),
      ...CarerLeavePageOne.questions.slice(1),
    ],
  }

  const CarerLeavePageOneWithOtherSymptoms: QuestionnairePage = {
    questions: [
      ...CarerLeavePageOne.questions,
      Question.freeText(
        'mcSymptom',
        'Please describe their symptoms, duration and associated symptoms.',
      ),
    ],
  }

  const CarerLeaveMultiDayPageOneWithOtherSymptoms: QuestionnairePage = {
    questions: [
      ...CarerLeavePageOne.questions,
      Question.freeText(
        'mcSymptom',
        'Please describe their symptoms, duration and associated symptoms.',
      ),
    ],
  }

  const CarerLeavePageTwo: QuestionnairePage = {
    questions: [
      Question.yesNo(
        'hasSeriousSymptoms',
        'Do they have the following symptoms?\n' +
          '- Chest pain/palpitation/shortness of breath\n' +
          '- Weakness and changes in sensation in any part of the body\n' +
          '- Blurred vision/Slurred speech\n' +
          '- Headache associated with decreased alertness and is different from previously diagnosed migraine/tension headache\n' +
          '- Neck stiffness\n' +
          '- Rashes that don’t fade when you press on it\n' +
          '- Excessive vomiting/not tolerating oral intake\n' +
          '- High fever > 39°C with rigors\n' +
          '- Self-harm thoughts/suicidal ideation\n',
      ),
      Question.singleChoice(
        'childHasSeriousSymptoms',
        'If you are looking after your child, do they have the following symptoms?\n' +
          '- Signs of dehydration (lack of urine, dry nappies, absence of tears, dry mouth and mucosa, lethargy, and difficulty waking them up).\n' +
          '- Rashes associated with drowsiness and neck stiffness.\n' +
          '- Inability to feed them or not tolerating oral intake.\n',
        [
          { id: 'Yes', label: 'Yes' },
          { id: 'No', label: 'No' },
          { id: 'NotApplicable', label: 'Not Applicable' },
        ],
      ),
    ],
  }

  const CarerLeavePageThree: QuestionnairePage = {
    questions: [
      Question.yesNo(
        'mcSeeGpAgreement',
        'Will you take them to a GP if they don’t get better within the next 24-48 hours?',
      ),
      Question.yesNo('mcInlandAgreement', 'Are you in Australia?'),
      Question.yesNo(
        'mcTnC',
        'Have you read and accept the terms and conditions?',
      ),
    ],
  }

  export const SickLeaveQuestionnaireSchema = PatientProfileValidator.shape({
    symptoms: string()
      .default('')
      .oneOf([
        'COMMON_COLD',
        'PERIOD_PAIN',
        'MILD_LOWER_BACK_PAIN',
        'MILD_HEADACHE',
        'MINOR_INJURY',
        'SORE_THROAT',
        'DIARRHOEA',
        'TOOTHACHE',
        'EXHAUSTING',
        'OTHERS',
      ] as const)
      .nullable(),
    onDate: string()
      .default(() => formatDate(new Date()))
      .required()
      .test(
        'on-date-must-be-today-or-tmr',
        () => 'You can only request for either today or tomorrow',
        (value) => {
          const diff = differenceInCalendarDays(parseDate(value), new Date())
          return diff === 0 || diff === 1
        },
      ),
    requiresDoctorVisit: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(mustAgree('Sorry you need to see a doctor in person.'))
      .nullable(),
    hasSeriousSymptoms: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test({
        name: 'is-false',
        skipAbsent: false,
        test(value, ctx) {
          if (value !== 'No') {
            return ctx.createError({
              message:
                'Sorry you need to go see a doctor in person or go to emergency.',
            })
          }
          return true
        },
      }),
    hasPastMedicalConditions: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .nullable(),
    pastMedicalConditions: string()
      .default('')
      .nullable()
      .test({
        name: 'conditional-on-past-medical-conditions',
        test(value, ctx) {
          if (ctx.parent.hasPastMedicalConditions === 'Yes' && !value) {
            return ctx.createError({
              message:
                'Please let us know about your medical conditions before proceeding.',
            })
          }
          return true
        },
      }),
    mcType: string()
      .default('')
      .oneOf(['Sick', 'Carer'] as const)
      .required(),
    mcFor: string()
      .default('')
      .oneOf(['Work', 'Study'] as const)
      .required(),
    mcSymptom: string()
      .default('')
      .test({
        name: 'conditional-on-symptom-choice',
        test(value, ctx) {
          if (ctx.parent.symptoms === 'OTHERS' && value.trim().length === 0) {
            return ctx.createError({
              message: 'Please let the doctor know about your symptoms.',
            })
          }

          return true
        },
      }),
    mcSeeGpAgreement: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(mustAgree('You must agree to see a GP if your condition worsen.')),
    mcInlandAgreement: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(
        mustAgree('You must be in Australia to request a medical certificate.'),
      ),
    mcTnC: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(mustAgree('You must agree to terms and agreements.')),
  })

  export const SickLeaveMultiDayQuestionnaireSchema =
    SickLeaveQuestionnaireSchema.shape({
      onDate: string()
        .default(() => formatDate(new Date()))
        .required()
        .test(
          'on-date-must-be-yesterday-or-today',
          () => 'The start date must be either yesterday or today',
          (value) =>
            [
              formatDate(addDays(new Date(), -1)),
              formatDate(new Date()),
            ].includes(value),
        ),
      toDate: string()
        .default(() => formatDate(addDays(new Date(), 1)))
        .required()
        .test(
          'to-date-must-be-exactly-1-day-apart-from-on-date',
          () => 'The end date must be exactly one day after the start date',
          (value, ctx) =>
            value === formatDate(addDays(parseDate(ctx.parent.onDate), 1)),
        ),
    })

  export const CarerLeaveQuestionnaireSchema = PatientProfileValidator.shape({
    symptoms: string()
      .default('')
      .oneOf([
        'COMMON_COLD',
        'PERIOD_PAIN',
        'MILD_LOWER_BACK_PAIN',
        'MILD_HEADACHE',
        'MINOR_INJURY',
        'SORE_THROAT',
        'DIARRHOEA',
        'TOOTHACHE',
        'EXHAUSTING',
        'OTHERS',
      ] as const)
      .nullable(),
    onDate: string()
      .default(() => formatDate(new Date()))
      .required()
      .test(
        'on-date-must-be-today-or-tmr',
        () => 'You can only request for either today or tomorrow',
        (value) => {
          const diff = differenceInCalendarDays(parseDate(value), new Date())
          return diff === 0 || diff === 1
        },
      ),
    requiresDoctorVisit: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test({
        // For carer leave, this is formatted differently so requires a different answer
        name: 'is-no',
        skipAbsent: false,
        test(value: 'Yes' | 'No', ctx) {
          if (value !== 'No') {
            return ctx.createError({
              message: 'Sorry they need to see a doctor in person.',
            })
          }
          return true
        },
      })
      .nullable(),
    hasSeriousSymptoms: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test({
        name: 'is-false',
        skipAbsent: false,
        test(value, ctx) {
          if (value !== 'No') {
            return ctx.createError({
              message:
                'Sorry they need to go see a doctor in person or go to emergency.',
            })
          }
          return true
        },
      }),
    mcType: string()
      .default('')
      .oneOf(['Sick', 'Carer'] as const)
      .required(),
    mcFor: string()
      .default('')
      .oneOf(['Work', 'Study'] as const)
      .required(),
    mcCarerFor: string()
      .default('')
      .oneOf(['FamilyMember', 'Partner', 'Housemate'])
      .required(),
    mcSymptom: string()
      .default('')
      .test({
        name: 'conditional-on-symptom-choice',
        test(value, ctx) {
          if (ctx.parent.symptoms === 'OTHERS' && value.trim().length === 0) {
            return ctx.createError({
              message: 'Please let the doctor know about their symptoms.',
            })
          }

          return true
        },
      }),
    childHasSeriousSymptoms: string()
      .default('')
      .oneOf(
        ['No', 'NotApplicable'],
        'You should take them to the GP if they\'re showing these symptoms"',
      )
      .required(),
    mcSeeGpAgreement: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(
        mustAgree(
          'You must agree to take them to a GP if their condition worsen.',
        ),
      ),
    mcInlandAgreement: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(
        mustAgree('You must be in Australia to request a medical certificate.'),
      ),
    mcTnC: string()
      .default('')
      .transform((value) => {
        if (value === 'true' || value === true) {
          return 'Yes'
        } else if (value === 'false' || value === false) {
          return 'No'
        }

        return value
      })
      .oneOf(['Yes', 'No'] as const)
      .test(mustAgree('You must agree to terms and agreements.')),
  })

  export const CarerLeaveMultiDayQuestionnaireSchema =
    CarerLeaveQuestionnaireSchema.shape({
      onDate: string()
        .default(() => formatDate(new Date()))
        .required()
        .test(
          'on-date-must-be-yesterday-or-today',
          () => 'The start date must be either yesterday or today',
          (value) =>
            [
              formatDate(addDays(new Date(), -1)),
              formatDate(new Date()),
            ].includes(value),
        ),
      toDate: string()
        .default(() => formatDate(addDays(new Date(), 1)))
        .required()
        .test(
          'to-date-must-be-exactly-1-day-apart-from-on-date',
          () => 'The end date must be exactly one day after the start date',
          (value, ctx) =>
            value === formatDate(addDays(parseDate(ctx.parent.onDate), 1)),
        ),
    })

  export interface QuestionnaireValues extends InferType<
    typeof SickLeaveQuestionnaireSchema &
      typeof CarerLeaveQuestionnaireSchema &
      typeof SickLeaveMultiDayQuestionnaireSchema &
      typeof CarerLeaveMultiDayQuestionnaireSchema
  > {
    // Guest SMS verification proof for `mobile`, sent with the request.
    mobileVerificationToken?: string
  }

  export interface SickLeaveQuestionnaireValues extends InferType<
    typeof SickLeaveQuestionnaireSchema
  > {}

  export interface CarerLeaveQuestionnaireValues extends InferType<
    typeof CarerLeaveQuestionnaireSchema
  > {}

  export function SickLeaveQuestionnaire(
    values: SickLeaveQuestionnaireValues,
  ): Questionnaire {
    return {
      pages: [
        values.symptoms === 'OTHERS'
          ? SickLeavePageOneWithOtherSymptoms
          : SickLeavePageOne,
        values.hasPastMedicalConditions === 'Yes'
          ? SickLeavePageTwoWithPastMedicalConditions
          : SickLeavePageTwo,
        SickLeavePageThree,
        'personal-information',
      ],
    }
  }

  export function SickLeaveMultiDayQuestionnaire(
    values: SickLeaveQuestionnaireValues,
  ): Questionnaire {
    return {
      pages: [
        values.symptoms === 'OTHERS'
          ? SickLeaveMultiDayPageOneWithOtherSymptoms
          : SickLeaveMultiDayPageOne,
        values.hasPastMedicalConditions === 'Yes'
          ? SickLeavePageTwoWithPastMedicalConditions
          : SickLeavePageTwo,
        SickLeavePageThree,
        'personal-information',
      ],
    }
  }

  export function CarerLeaveQuestionnaire(
    values: CarerLeaveQuestionnaireValues,
  ): Questionnaire {
    return {
      pages: [
        values.symptoms === 'OTHERS'
          ? CarerLeavePageOneWithOtherSymptoms
          : CarerLeavePageOne,
        CarerLeavePageTwo,
        CarerLeavePageThree,
        'personal-information',
      ],
    }
  }

  export function CarerLeaveMultiDayQuestionnaire(
    values: CarerLeaveQuestionnaireValues,
  ): Questionnaire {
    return {
      pages: [
        values.symptoms === 'OTHERS'
          ? CarerLeaveMultiDayPageOneWithOtherSymptoms
          : CarerLeaveMultiDayPageOne,
        CarerLeavePageTwo,
        CarerLeavePageThree,
        'personal-information',
      ],
    }
  }

  const SickLeaveDeprecatedQuestions = [
    Question.yesNo(
      'isGeneralIllness',
      'Are you requesting a medical certificate due to a general illness?',
    ),
  ]

  const CarerLeaveDeprecatedQuestions = [
    Question.yesNo(
      'isGeneralIllness',
      'Do they have a general illness requiring you to take care of them?',
    ),
  ]

  export function GetAllQuestions(
    type: McType,
    metadata: MedicalCertificateMetadata,
    multipleDays: boolean,
  ): Array<Question> {
    const questions: Array<Question> = []
    if (type === 'Sick') {
      const schema = multipleDays
        ? SickLeaveMultiDayQuestionnaireSchema
        : SickLeaveQuestionnaireSchema
      const casted = schema.cast(metadata)
      const questionnaire = multipleDays
        ? SickLeaveMultiDayQuestionnaire(casted)
        : SickLeaveQuestionnaire(casted)

      questions.push(
        ...questionnaire.pages
          .filter(
            (page) =>
              page !== 'personal-information' &&
              page !== 'upload-supporting-document' &&
              page !== 'call-arrangement' &&
              'questions' in page,
          )
          .flatMap((page) => page.questions),
      )
      questions.push(...SickLeaveDeprecatedQuestions)
    } else {
      const schema = multipleDays
        ? CarerLeaveMultiDayQuestionnaireSchema
        : CarerLeaveQuestionnaireSchema
      const casted = schema.cast(metadata)
      const questionnaire = multipleDays
        ? CarerLeaveMultiDayQuestionnaire(casted)
        : CarerLeaveQuestionnaire(casted)

      questions.push(
        ...questionnaire.pages
          .filter(
            (page) =>
              page !== 'personal-information' &&
              page !== 'upload-supporting-document' &&
              page !== 'call-arrangement' &&
              'questions' in page,
          )
          .flatMap((page) => page.questions),
      )
      questions.push(...CarerLeaveDeprecatedQuestions)
    }
    return questions
  }
}
